"use client";

import Link from "next/link";
import Image from "next/image";
import { useIntl } from "react-intl";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { appTargetOfUrl, useAppLink } from "@/lib/analytics/app-links";
import { LoopVideo } from "./LoopVideo";

// Fallback illustration when the article doesn't provide a mascotte image.
const DEFAULT_IMAGE = "/FlotsIcon.svg";

type ReminderProps = {
  label?: string;
  icon?: string;
  title?: string;
  text?: string;
  image?: string;
  alt?: string;
  button?: string;
  url?: string;
};

export function Reminder({
  label,
  icon,
  title,
  text,
  image,
  alt,
  button,
  url,
}: ReminderProps) {
  const intl = useIntl();

  const badge = label?.trim() || intl.formatMessage({ id: "ressources.rappel.label" });
  const iconChar = icon?.trim() || "💡";
  const src = image?.trim() || DEFAULT_IMAGE;
  const isDefaultImage = src === DEFAULT_IMAGE;
  const isGif = src.toLowerCase().endsWith(".gif");
  const isVideo = !isDefaultImage && /\.(mp4|webm)$/i.test(src);
  const href = url?.trim();
  const isExternal = href ? /^https?:\/\//.test(href) : false;
  const buttonLabel = button?.trim();
  // Only a web-app URL gets the handoff parameters; any other link is rendered unchanged.
  const { href: linkHref } = useAppLink("article-reminder", { target: "web", webBase: href });
  const linkTarget = href ? appTargetOfUrl(href) : null;

  return (
    <div className="not-prose my-10 flex items-center gap-4 overflow-hidden rounded-3xl border border-blue-light bg-blue-ulight p-5 sm:gap-6 sm:p-6">
      {/* Mascotte / illustration */}
      <span
        className={`relative block shrink-0 overflow-hidden ${
          isDefaultImage
            ? "h-16 w-16 sm:h-20 sm:w-20"
            : "h-20 w-20 rounded-2xl sm:h-24 sm:w-24"
        }`}
      >
        {isVideo ? (
          <LoopVideo
            src={src}
            label={alt || title || badge}
            wrapperClassName="absolute inset-0"
            className="h-full w-full object-cover"
          />
        ) : (
          <Image
            src={src}
            // The default Flots logo is decoration; a custom picture keeps its description.
            alt={isDefaultImage ? "" : alt || title || badge}
            fill
            unoptimized={isGif}
            className={isDefaultImage ? "object-contain" : "object-cover"}
          />
        )}
      </span>

      {/* Speech bubble: label + title + text + action */}
      <div className="flex flex-1 flex-col items-start gap-1.5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1 text-xs font-semibold text-brand-ink">
          <span aria-hidden="true">{iconChar}</span>
          {badge}
        </span>
        {title && (
          <p className="text-base font-bold leading-tight text-text-main sm:text-lg">{title}</p>
        )}
        {text && <p className="text-sm leading-relaxed text-gray-main">{text}</p>}
        {href && buttonLabel && (
          <Link
            href={linkHref}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            onClick={() => {
              trackEvent("ressources-article-rappel", { url: href }, { criticalOnNav: true });
              if (linkTarget) trackCtaClick("article-reminder", linkTarget);
            }}
            className="inline-flex items-center gap-1.5 py-1 text-sm font-semibold text-brand-ink no-underline transition-all duration-200 hover:gap-2.5"
          >
            {buttonLabel}
            <span aria-hidden="true">→</span>
          </Link>
        )}
      </div>
    </div>
  );
}
